import type { ReactElement } from 'react';
import GeneralAlert from '@components/Alert/GeneralAlert';
import { DashboardPageContent } from '@components/layout/DashboardPageContent';
import { Layout } from '@components/layout/Layout';
import { OrderingStatusAlert } from '@components/orders/OrderingStatusAlert';
import strings from '@config/strings';
import { useApp } from '@context/AppContext';
import { CmsContentRenderer } from '@features/cms/components/cms-content-renderer';
import { NextEventBanner } from '@features/schedule/components/next-event-banner';
import { FlexView } from '@goodfynd/ui';
import useAlerts from '@hooks/useAlerts';
import { usePermissions } from '@hooks/usePermissions';
import { useVendor } from 'context/VendorContext';
import { OrdersOverviewProvider } from 'features/overview/orders-overview-context';
import OverviewCards from 'features/overview/overview-cards-container';
import Head from 'next/head';
import { setPageTitle } from 'utils/base';

function DashboardOverview() {
  const { vendor } = useVendor();
  const { isManager } = usePermissions();
  const { user } = useApp();
  const { alerts, dismissAlert, clickedAlert } = useAlerts(user.id);

  return (
    <>
      <Head>
        <title>
          {setPageTitle(
            !!vendor?.id
              ? `${strings.titles.dashboard} - ${vendor.uniqueName}`
              : strings.titles.dashboard
          )}
        </title>
      </Head>

      <DashboardPageContent title={strings.titles.welcomeBack}>
        <FlexView direction="column" gap={24}>
          {isManager && (
            <OrderingStatusAlert organizationId={vendor.organizationId} />
          )}

          <NextEventBanner />

          {alerts.map((alert) => (
            <GeneralAlert
              key={alert.objectId}
              linkText={alert.linkText}
              message={alert.message}
              title={alert.title}
              link={
                alert.link?.charAt(0) === '/'
                  ? `${window.location.origin}/dashboard/${vendor.nameId}${alert.link}`
                  : alert.link
              }
              onClicked={() => {
                clickedAlert(alert.objectId);
              }}
              onDismiss={() => {
                dismissAlert(alert.objectId);
              }}
              type={
                alert.severity === 'Error'
                  ? 'error'
                  : (() => {
                      switch (alert.severity) {
                        case 'Warning':
                          return 'warning';
                        case 'Info':
                          return 'info';
                        default:
                          return undefined;
                      }
                    })()
              }
            >
              <></>
            </GeneralAlert>
          ))}

          <CmsContentRenderer slug="vendor-overview-ad" />

          <OrdersOverviewProvider vendorId={vendor.id}>
            <OverviewCards />
          </OrdersOverviewProvider>
        </FlexView>
      </DashboardPageContent>
    </>
  );
}

DashboardOverview.getLayout = function getLayout(page: ReactElement) {
  return <Layout>{page}</Layout>;
};

export default DashboardOverview;
